import { useEffect } from 'react';
import { Box, DuoColorIcon, Icon, Text } from '@zillow/constellation-10';
import { IconShieldCheckmarkFilled } from '@zillow/constellation-icons';
import { useState } from 'react';
import ActiveCard from './ActiveCard';
import EmptyCard from './EmptyCard';
import ErrorCard from './ErrorCard';
import ProofOfInsuranceViewer from './ProofOfInsuranceViewer';
import RentersInsuranceUploader from './RentersInsuranceUploader';
import type { CtaAction, YourRentalErrorDto, YourRentalRinsCardDto } from '@zg-rentals/ts-your-rental-api-client';
import type { YourRentalRentersInsuranceDto } from '../../mocks/types';

const UPSELL_DISMISSED_STORAGE_KEY = 'hasRentersInsuranceUpsellBeenDismissedForListingAlias-';

const CONTENT = {
  EMPTY_TITLE: 'Get renters insurance',
  EMPTY_BODY: 'Protect your personal items from events like theft, fire, or damage.',
  EMPTY_CTA: 'Get started',
  VIEWER_TITLE: 'Proof of insurance',
} as const;

type RentersInsuranceCardProps = {
  rentersInsuranceCard?: YourRentalRinsCardDto;
  listingAlias?: string;
};

type RentersInsuranceActiveCardProps = {
  insurance: YourRentalRentersInsuranceDto;
};

const RentersInsuranceEmptyCard = ({
  rinsCard,
  listingAlias,
  onDismiss,
}: {
  rinsCard: YourRentalRinsCardDto;
  listingAlias?: string;
  onDismiss: () => void;
}) => {
  const handleClose = () => {
    if (listingAlias) {
      localStorage.setItem(`${UPSELL_DISMISSED_STORAGE_KEY}${listingAlias}`, JSON.stringify(true));
    }
    onDismiss();
  };

  return (
    <EmptyCard
      icon={
        <DuoColorIcon tone="inspire">
          <Icon>
            <IconShieldCheckmarkFilled />
          </Icon>
        </DuoColorIcon>
      }
      title={rinsCard.cardTitle}
      description={rinsCard.cardText}
      link={rinsCard.cta.url}
      buttonText={rinsCard.cta.text}
      onClose={listingAlias ? handleClose : undefined}
      buttonAriaLabel={rinsCard.cta.action}
      dataTestId="RentersInsurance"
    />
  );
};

const RentersInsuranceErrorCard = ({ error }: { error: YourRentalErrorDto }) => {
  return (
    <ErrorCard
      icon={
        <DuoColorIcon tone="info">
          <Icon>
            <IconShieldCheckmarkFilled />
          </Icon>
        </DuoColorIcon>
      }
      title={error.cardTitle}
      error={error}
      dataTestId="RentersInsurance"
    />
  );
};

const RentersInsuranceActiveCard = ({ insurance }: RentersInsuranceActiveCardProps) => {
  const [shouldOpenFilePicker, setShouldOpenFilePicker] = useState(false);
  const [uploadAction, setUploadAction] = useState<CtaAction | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleViewerClose = () => {
    setIsViewerOpen(false);
    setPreviewUrl(null);
  };

  const handleUploadEnd = () => {
    setShouldOpenFilePicker(false);
    setUploadAction(null);
  };

  return (
    <>
      <ActiveCard
        title={insurance.cardTitle}
        dataTestId="RentersInsuranceActiveCard"
        icon={
          <DuoColorIcon tone="info">
            <Icon>
              <IconShieldCheckmarkFilled />
            </Icon>
          </DuoColorIcon>
        }
        tags={[insurance.statusTag]}
        ctas={insurance.ctas}
      >
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'micro.default',
          }}
        >
          <Text textStyle="body-lg-bold">{insurance.actionTitle}</Text>
          <Text>{insurance.actionText}</Text>
        </Box>
      </ActiveCard>
      <RentersInsuranceUploader
        shouldOpenFilePicker={shouldOpenFilePicker}
        onUploadComplete={handleUploadEnd}
        onUploadFailed={handleUploadEnd}
        onFilePickerOpened={() => setShouldOpenFilePicker(false)}
        uploadAction={uploadAction}
      />
      <ProofOfInsuranceViewer
        isOpen={isViewerOpen}
        onClose={handleViewerClose}
        previewUrl={previewUrl}
        title={CONTENT.VIEWER_TITLE}
      />
    </>
  );
};

const hasUpsellBeenDismissed = (listingAlias?: string): boolean => {
  if (!listingAlias) {
    return false;
  }

  const localStorageValue = localStorage.getItem(`${UPSELL_DISMISSED_STORAGE_KEY}${listingAlias}`);
  if (localStorageValue !== null) {
    return JSON.parse(localStorageValue);
  }

  return false;
};

const RentersInsuranceCard = ({ rentersInsuranceCard, listingAlias }: RentersInsuranceCardProps) => {
  const [isUpsellDismissed, setIsUpsellDismissed] = useState(() => {
    return hasUpsellBeenDismissed(listingAlias);
  });

  useEffect(() => {
    setIsUpsellDismissed(hasUpsellBeenDismissed(listingAlias));
  }, [listingAlias]);

  const handleDismiss = () => {
    setIsUpsellDismissed(true);
  };

  if (rentersInsuranceCard && !isUpsellDismissed) {
    return (
      <RentersInsuranceEmptyCard
        rinsCard={rentersInsuranceCard}
        listingAlias={listingAlias}
        onDismiss={handleDismiss}
      />
    );
  }

  return null;
};

export default RentersInsuranceCard;
